import { useMemo, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Briefcase,
  Building2,
  ChevronUp,
  ClipboardCheck,
  FileText,
  GitBranch,
  Handshake,
  KanbanSquare,
  IdCard,
  LayoutGrid,
  LogOut,
  ScrollText,
  Search,
  Settings,
  Shield,
  Tags,
  Users,
  type LucideIcon,
} from 'lucide-react'
import { BrandMark } from '@/components/brand-mark'
import { EnvironmentPill } from '@/components/environment-pill'
import { ThemeSegmentedControl } from '@/components/theme-toggle'
import { AvatarGradient } from '@/components/ui/avatar'
import { meAvatarUrl } from '@/lib/api/me'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import { PortalViewSwitcher } from '@/components/portal-view-switcher'
import { listProfessionals, listApplications } from '@/lib/api/applications'
import { listAssessments } from '@/lib/api/assessments'
import { listCustomers } from '@/lib/api/customers'
import { listJobProfiles } from '@/lib/api/job-profiles'
import { listOpportunities } from '@/lib/api/opportunities'
import { listPipelines } from '@/lib/api/pipelines'
import { useAuth } from '@/hooks/use-auth'
import { SEARCH_SHORTCUT_ARIA, searchShortcutLabel } from '@/hooks/use-global-search-hotkey'
import { useSlidingIndicator } from '@/hooks/use-sliding-indicator'
import { useGlobalSearchStore } from '@/stores/global-search-store'
import { hasPortalViews } from '@/lib/portal-view'
import { cn } from '@/lib/utils'

interface NavItemDef {
  id: string
  to: string
  label: string
  icon: LucideIcon
  /**
   * Whether this item shows a count badge. The actual number is resolved from
   * live data in {@link AppSidebar} (Story 9.3) and injected at render — the
   * static nav definition no longer hardcodes counters.
   */
  showCount?: boolean
  /**
   * Só para quem abre o Dashboard de gestão de contas (Story 38.8): o admin, quem é
   * gestor da conta de um cliente ativo e quem é Delivery Manager de uma operação
   * (`user.accountManagement`, da `/api/me`). Quem não o abre não tem o item no DOM.
   */
  accountManagementOnly?: boolean
}

/** Exportado porque é o tipo do prop `navGroups` — quem o passa precisa nomeá-lo. */
export interface NavGroup {
  label: string
  links: NavItemDef[]
}

/**
 * Estrutura do trilho, conforme o protótipo Lumen (design/lumen/app.jsx, `NAV`).
 *
 * Dois grupos, divididos pelo que a pessoa FAZ e não por onde o dado mora:
 *
 *   • "Trabalho" — o fluxo diário: o painel e as pessoas que passam por ele.
 *   • "Catálogo" — o que é configurado uma vez e depois só se consulta.
 *
 * Os badges (`showCount`) são ligados a totais reais em {@link AppSidebar}
 * (Story 9.3); enquanto a query não resolve, o número é um esqueleto.
 */
export const DEFAULT_NAV_GROUPS: NavGroup[] = [
  {
    label: 'Trabalho',
    links: [
      { id: 'dashboard', to: '/dashboard', label: 'Início', icon: LayoutGrid },
      // O quadro vive em TRABALHO, não em Catálogo: é onde se passa o dia.
      //
      // E vem LOGO A SEGUIR AO INÍCIO porque é o item mais usado do produto
      // (decisão do owner, 2026-08-03) — antes estava em terceiro, atrás das
      // candidaturas. Num trilho a ordem é uma afirmação sobre frequência de
      // uso: deixar em terceiro o item mais aberto contradizia-a.
      //
      // Sem badge de contagem — o número depende da vaga escolhida, e um total
      // global não diria nada.
      { id: 'kanban', to: '/kanban', label: 'Kanban', icon: KanbanSquare },
      {
        id: 'applications',
        to: '/applications',
        label: 'Candidaturas',
        icon: ClipboardCheck,
        showCount: true,
      },
      {
        id: 'professionals',
        to: '/professionals',
        label: 'Profissionais',
        icon: Users,
        showCount: true,
      },
      // Story 38.8 — o Dashboard de gestão de contas, no FIM do "Trabalho": o Kanban
      // fica logo a seguir ao Início (decisão do owner, acima). A 38.9 move-o para a
      // frente da gestão de contas quando o menu passar a ser por frente.
      {
        id: 'account-management',
        to: '/account-management',
        label: 'Gestão de contas',
        icon: Handshake,
        accountManagementOnly: true,
      },
    ],
  },
  {
    label: 'Catálogo',
    links: [
      {
        id: 'opportunities',
        to: '/opportunities',
        label: 'Vagas',
        icon: Briefcase,
        showCount: true,
      },
      // A vaga vem primeiro porque é o que se abre todos os dias; o cliente e o
      // perfil são o que ela referencia — cadastram-se antes, consultam-se
      // depois. A ordem é a da dependência lida de trás para a frente.
      { id: 'customers', to: '/customers', label: 'Clientes', icon: Building2, showCount: true },
      {
        id: 'job-profiles',
        to: '/job-profiles',
        label: 'Perfis de vaga',
        icon: IdCard,
        showCount: true,
      },
      {
        id: 'pipelines',
        // A TELA vive sob /settings (é configuração do produto), mas o ITEM
        // mora aqui: um pipeline é o que a vaga referencia, tal como o cliente
        // e o perfil. Atrás do menu de Configurações estaria escondido de quem
        // o vai usar todos os dias.
        to: '/settings/pipelines',
        label: 'Pipelines',
        icon: GitBranch,
        showCount: true,
      },
      {
        id: 'assessments',
        // "Modelos de avaliação", e não "Avaliações": no Caliber uma AVALIAÇÃO
        // é o resultado corrigido de um candidato ("Avaliação criada",
        // "Avaliações vinculadas", a coluna das candidaturas). O que se cataloga
        // aqui é o molde que se aplica — dar-lhes o mesmo nome tornaria frases
        // inteiras do produto incoerentes.
        to: '/assessments',
        label: 'Modelos de avaliação',
        icon: FileText,
        showCount: true,
      },
    ],
  },
]

/**
 * Admin-only nav group (Story 10.2 AC-13). Appended to {@link DEFAULT_NAV_GROUPS}
 * in {@link AppSidebar} ONLY when `user.role === 'admin'` — for a recruiter the
 * "Usuários" item is absent from the DOM (not merely hidden via CSS). This
 * is UX-only; the real gate is `requireAdmin` on /api/users.
 */
export const ADMIN_NAV_GROUP: NavGroup = {
  label: 'Administração',
  links: [
    { id: 'users', to: '/settings/users', label: 'Usuários', icon: Shield },
    // #485 — `Tags`, e não `Sparkles`: o brilho é a marca do assistente de IA.
    { id: 'skills', to: '/settings/skills', label: 'Skills', icon: Tags },
    // #593 — a trilha das escritas: quem criou, alterou ou removeu o quê.
    { id: 'audit', to: '/settings/audit', label: 'Auditoria', icon: ScrollText },
  ],
}

/** Resolved badge state for one nav item — Story 9.3. */
interface NavCount {
  /** A contagem real, ou undefined enquanto carrega / em erro. */
  value: number | undefined
  loading: boolean
}

interface NavItemProps extends NavItemDef {
  /** Live count badge state (present only for `showCount` items). */
  count?: NavCount
}

/**
 * NavItem — o `.rail-link` do protótipo.
 *
 * O estado ativo NÃO é uma classe condicional: o `NavLink` já marca
 * `aria-current="page"`, e é esse atributo que o CSS usa para pintar a
 * superfície elevada + o traço de luz de 2px na aresta esquerda. Assim o
 * contrato visual e o contrato de acessibilidade são o MESMO — não é possível
 * ficar visualmente ativo sem estar acessivelmente ativo.
 */
function NavItem({ to, label, icon: Icon, showCount, count }: NavItemProps) {
  return (
    <NavLink to={to} className="rail-link">
      <Icon className="size-[17px] shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {showCount ? <CountBadge count={count} /> : null}
    </NavLink>
  )
}

/**
 * CountBadge — a contagem à direita dentro do link (`.count`, Story 9.3).
 *
 * Enquanto a query carrega mostramos um placeholder em forma de esqueleto
 * (NFR-DS-2: sem spinners). Em erro a contagem é simplesmente omitida (sem
 * badge), para que uma falha transitória da API nunca mostre um "0" mentiroso.
 */
function CountBadge({ count }: { count?: NavCount | undefined }) {
  if (count?.loading) {
    return <Skeleton className="count h-[13px] w-5 rounded-full" aria-hidden="true" />
  }

  if (count?.value === undefined) return null

  return (
    <span aria-label={`${count.value} itens`} className="count">
      {count.value}
    </span>
  )
}

export interface AppSidebarProps {
  /** Override the default nav groups (mainly for tests / Storybook). */
  navGroups?: NavGroup[]
  /**
   * Onde o menu mora (#493). O CONTEÚDO é o mesmo nos dois — grupos, contagens,
   * busca, cartão do usuário —, e é por isso que é um componente só: duas cópias
   * dos grupos e das queries divergiriam à primeira entrada nova.
   *
   *   • `rail` (omissão) — o trilho fixo de 232px da moldura. Só existe a partir
   *     de `lg`; abaixo disso some, e não ocupa espaço.
   *   • `drawer` — dentro da gaveta que o cabeçalho do celular abre
   *     (`layouts/cabecalho-do-celular.tsx`). Ocupa a gaveta inteira e NÃO traz
   *     vidro: a superfície é a da gaveta, e vidro sobre vidro seria uma segunda
   *     composição por frame a desfocar a primeira.
   */
  variant?: 'rail' | 'drawer'
  /**
   * Para onde o popover do cartão do usuário é portado. Omitido (o trilho), vai
   * para o `<body>`, o default do Radix.
   *
   * Na gaveta do celular é um nó de dentro dela. A gaveta é o diálogo do Radix,
   * e quando isto nasceu (#493) o `@radix-ui/react-dialog` resolvia OUTRAS
   * versões do `react-focus-scope` e do `react-dismissable-layer` que o
   * `@radix-ui/react-popover` (1.1.7/1.1.11 contra 1.1.10/1.1.13): eram duas
   * pilhas que não se viam. Portado para o `<body>`, o popover ficava FORA da
   * gaveta — a armadilha de foco dela puxava o foco de volta e ele fechava, e
   * herdava o `pointer-events: none` que ela põe no resto da página. Dentro da
   * gaveta, é conteúdo dela. O #502 alinhou as versões (uma de cada primitivo
   * no lockfile) e o caso do `<body>` passou a funcionar; o portal para dentro
   * fica: é o desenho que o #493 verificou em produção.
   */
  portalContainer?: HTMLElement | null
}

/**
 * AppSidebar — o trilho de 232px (`nav.rail.glass` do protótipo Lumen).
 *
 * É chrome, portanto vive no plano 2 do sistema (vidro): fica entre o substrato
 * e o conteúdo elevado, e nunca compete com ele.
 *
 * Anatomia (topo → base):
 *   • Wordmark: BrandMark (o "G" da Genesis Consulting) + "Caliber"
 *   • Grupos (`rail-group`) com os links (`rail-link` + `count` opcional)
 *   • Espaçador
 *   • Bloco de usuário: avatar + nome + email, que abre o menu de configurações
 *
 * Empty state (AC22): se um grupo tiver `links: []`, o rótulo continua a
 * renderizar e o layout não quebra. O rodapé fica visível de qualquer forma.
 */
export function AppSidebar({
  navGroups = DEFAULT_NAV_GROUPS,
  variant = 'rail',
  portalContainer,
}: AppSidebarProps) {
  const { user, signOut } = useAuth()
  const { pathname } = useLocation()

  // Story 10.2 (AC-13) — append the admin "Usuários" group ONLY for admins.
  // A recruiter never gets the item in the DOM. Computed from the passed groups
  // so test overrides still apply; the admin group is additive on top of them.
  //
  // Story 38.8 — os itens `accountManagementOnly` só ficam para quem abre o Dashboard
  // de gestão de contas, também nos grupos passados pelos testes.
  const effectiveNavGroups = useMemo<NavGroup[]>(() => {
    const visiveis = navGroups.map((g) => ({
      ...g,
      links: g.links.filter((l) => !l.accountManagementOnly || user?.accountManagement === true),
    }))
    return user?.role === 'admin' ? [...visiveis, ADMIN_NAV_GROUP] : visiveis
  }, [navGroups, user?.role, user?.accountManagement])

  // O marcador de página ativa é UM elemento que desce e sobe o trilho, em vez
  // de acender no item de destino. Quem está ativo continua a ser decidido pelo
  // `aria-current` que o NavLink escreve — o marcador segue esse atributo, não
  // uma segunda opinião sobre qual é a rota.
  //
  // A chave leva a rota E a lista de itens renderizados: um item que aparece
  // depois (o grupo de administração, um item atrás de flag) muda quem está
  // ativo sem que a rota mude, e sem isso o marcador não seria remedido.
  const railKey = useMemo(
    () => `${pathname}|${effectiveNavGroups.flatMap((g) => g.links.map((l) => l.id)).join(',')}`,
    [pathname, effectiveNavGroups],
  )
  const { containerRef, box } = useSlidingIndicator<HTMLDivElement>(
    '[aria-current="page"]',
    railKey,
  )
  // O MESMO estado que o `⌘K` do `AppLayout` mexe — loja de módulo, sem
  // Provider: o gatilho e o atalho não podem discordar sobre se a paleta está
  // aberta.
  const openGlobalSearch = useGlobalSearchStore((s) => s.open)

  // Controlado para que cliques no tema (dentro do popover) o mantenham aberto,
  // enquanto "Configurações"/"Sair" o fecham explicitamente (Story 10.1).
  const [userMenuOpen, setUserMenuOpen] = useState(false)

  // ----- Live count badges (Story 9.3) -----
  // Each query asks for a single row (`pageSize=1`) and reads only the `total`
  // envelope field — the list endpoints all expose `{ items, page, pageSize,
  // total }`. Counts are cached briefly so navigation doesn't re-fetch.
  const applications = useQuery({
    queryKey: ['applications', 'count'],
    queryFn: ({ signal }) => listApplications({ pageSize: 1, signal }),
    staleTime: 30_000,
  })
  const professionals = useQuery({
    queryKey: ['professionals', 'count'],
    queryFn: ({ signal }) => listProfessionals({ pageSize: 1, signal }),
    staleTime: 30_000,
  })
  const opportunities = useQuery({
    queryKey: ['opportunities', 'count'],
    queryFn: ({ signal }) => listOpportunities({ pageSize: 1, signal }),
    staleTime: 30_000,
  })
  const assessments = useQuery({
    queryKey: ['assessments', 'count'],
    queryFn: ({ signal }) => listAssessments({ pageSize: 1, signal }),
    staleTime: 30_000,
  })
  // Migration 0022 — os dois catálogos novos. `showCount` na definição do item
  // não produz número nenhum sozinho: é ESTA query, e a entrada no mapa abaixo,
  // que o badge lê.
  const customers = useQuery({
    queryKey: ['customers', 'count'],
    queryFn: ({ signal }) => listCustomers({ pageSize: 1, signal }),
    staleTime: 30_000,
  })
  const jobProfiles = useQuery({
    queryKey: ['job-profiles', 'count'],
    queryFn: ({ signal }) => listJobProfiles({ pageSize: 1, signal }),
    staleTime: 30_000,
  })
  // Migration 0023 — só os ATIVOS, que é o que o cadastro mostra por omissão.
  // Contar os arquivados aqui daria um badge maior do que a lista que ele abre.
  const pipelinesCount = useQuery({
    queryKey: ['pipelines', 'count'],
    queryFn: ({ signal }) => listPipelines({ pageSize: 1, signal }),
    staleTime: 30_000,
  })

  const countById = useMemo<Record<string, NavCount>>(
    () => ({
      applications: { value: applications.data?.total, loading: applications.isPending },
      // ⚠️ A CHAVE TEM DE CASAR COM O `id` DO ITEM (`DEFAULT_NAV_GROUPS`, acima).
      // `countById` é um `Record<string, NavCount>`, portanto o `tsc` NÃO liga as
      // duas pontas: renomear o `id` sem renomear esta chave apaga o badge em
      // silêncio, com os tipos verdes e os testes verdes.
      professionals: { value: professionals.data?.total, loading: professionals.isPending },
      opportunities: { value: opportunities.data?.total, loading: opportunities.isPending },
      customers: { value: customers.data?.total, loading: customers.isPending },
      'job-profiles': { value: jobProfiles.data?.total, loading: jobProfiles.isPending },
      pipelines: { value: pipelinesCount.data?.total, loading: pipelinesCount.isPending },
      assessments: { value: assessments.data?.total, loading: assessments.isPending },
    }),
    [
      applications.data?.total,
      applications.isPending,
      professionals.data?.total,
      professionals.isPending,
      opportunities.data?.total,
      opportunities.isPending,
      customers.data?.total,
      customers.isPending,
      jobProfiles.data?.total,
      jobProfiles.isPending,
      pipelinesCount.data?.total,
      pipelinesCount.isPending,
      assessments.data?.total,
      assessments.isPending,
    ],
  )

  const initials = useMemo(() => {
    const source = user?.name ?? 'SG'
    return (
      source
        .split(' ')
        .map((part) => part[0])
        .filter(Boolean)
        .slice(0, 2)
        .join('')
        .toUpperCase() || 'SG'
    )
  }, [user?.name])

  // Story 15.2 — a foto real, quando existe. `hasAvatar` é o interruptor;
  // `avatarUpdatedAt` é o cache-buster que faz a foto nova aparecer aqui no
  // instante em que `/settings` a grava, sem recarregar a página.
  const avatarSrc = user?.hasAvatar ? meAvatarUrl(user.avatarUpdatedAt) : null

  return (
    <nav
      aria-label="Navegação principal"
      // `hidden lg:flex`, e nunca `flex` ao lado de `hidden`: com as duas, quem
      // ganhava era a ordem do CSS gerado, não a intenção.
      className={cn(
        'flex-col',
        variant === 'rail'
          ? 'glass border-hair hidden h-screen w-[232px] border-r lg:flex'
          : 'flex h-full w-full',
      )}
    >
      {/* Wordmark */}
      <div className="flex items-center gap-2.5 px-3 pb-2 pt-5">
        <BrandMark />
        <span
          className="text-ink font-semibold"
          style={{ fontSize: 15, letterSpacing: '-0.018em' }}
        >
          Caliber
        </span>
        <EnvironmentPill />
      </div>

      {/* O gatilho da busca global (Story 18.1, AC-5).

          ⚠️ FORA DO CONTENTOR QUE ROLA, e por DUAS razões independentes:
          (1) num trilho longo ele não pode sair de vista — é o atalho para
          chegar a qualquer registo do produto; (2) e, sobretudo, ele NÃO pode
          entrar no caminho do marcador deslizante da página ativa, que vive
          dentro de `.rail-links` e é medido por `[aria-current="page"]`
          (`useSlidingIndicator`, acima). Um botão a mais lá dentro mudaria a
          geometria que o marcador mede.

          ⚠️ NUNCA RECEBE `aria-current`: isto ABRE UM DIÁLOGO, não navega. É
          `aria-haspopup="dialog"`, e o `aria-keyshortcuts` é o que faz um leitor
          de ecrã anunciar o atalho — a cápsula visível com a tecla só serve quem
          vê.

          A forma é a de um CAMPO (cápsula), decisão contratual do owner. Não é
          um `<input>`: escrever acontece dentro do diálogo. */}
      <div className="px-3 pt-0.5">
        <button
          type="button"
          aria-haspopup="dialog"
          aria-keyshortcuts={SEARCH_SHORTCUT_ARIA}
          onClick={openGlobalSearch}
          className="bg-sunk border-hair text-ink-4 hover:text-ink-2 hover:border-hair-strong rounded-pill flex h-[34px] w-full items-center gap-[9px] border pl-3 pr-[11px] text-sm"
        >
          <Search size={14} aria-hidden="true" />
          <span className="flex-1 text-left">Buscar</span>
          <span className="bg-sunk border-hair text-ink-4 rounded-xs inline-flex h-[19px] items-center border px-1.5 font-mono text-[10.5px] font-medium">
            {searchShortcutLabel()}
          </span>
        </button>
      </div>

      {/* Grupos. O padding horizontal de 12px é o que dá espaço ao traço de luz
          do item ativo, que o CSS desenha em `left: -11px`.

          `relative` faz deste contentor a âncora do marcador — e, por ser o
          mesmo elemento que rola, o marcador rola com os links em vez de ficar
          colado ao trilho. */}
      <div
        ref={containerRef}
        className="rail-links relative flex-1 overflow-y-auto px-3"
        data-thumb={box ? 'on' : undefined}
      >
        {/* Um só marcador para os dois grupos: atravessar a fronteira entre
            "Trabalho" e "Catálogo" é precisamente o movimento que vale a pena
            mostrar. Nasce já no sítio certo (medição antes da pintura), por
            isso não desliza ao abrir a app. */}
        {box ? (
          <span
            aria-hidden="true"
            className="rail-thumb"
            style={{
              transform: `translate(${box.left}px, ${box.top}px)`,
              width: `${box.width}px`,
              height: `${box.height}px`,
            }}
          />
        ) : null}
        {effectiveNavGroups.map((group) => (
          <div key={group.label}>
            <p className="rail-group">{group.label}</p>
            <div className="flex flex-col gap-0.5">
              {group.links.map((item) => (
                <NavItem
                  key={item.id}
                  {...item}
                  {...(item.showCount ? { count: countById[item.id] } : {})}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Rodapé: o cartão de usuário é o gatilho de um Popover (Story 10.1) que
          guarda o controlo de tema + "Configurações" + "Sair"; o ThemeToggle
          solto foi removido do rodapé (AC-15). */}
      <div className="border-hair mt-3 border-t px-3 py-3">
        <Popover open={userMenuOpen} onOpenChange={setUserMenuOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-haspopup="dialog"
              aria-expanded={userMenuOpen}
              // WCAG 2.5.3 (Label in Name): o nome acessível tem de conter TODO
              // o texto visível do botão — que aqui é o nome E o email. Um
              // aria-label parcial continua a falhar a regra.
              aria-label={`Menu do usuário: ${user?.name ?? 'Convidado'} ${user?.email ?? ''}`.trim()}
              className={cn(
                'flex w-full items-center gap-2.5 rounded-[10px] border border-transparent p-2 text-left',
                'transition-colors duration-150 ease-out',
                'hover:bg-sunk hover:border-hair',
                userMenuOpen && 'bg-sunk border-hair',
              )}
            >
              <AvatarGradient initials={initials} src={avatarSrc} size={28} />
              <div className="min-w-0 flex-1 leading-tight">
                <p className="text-ink truncate text-[12.5px] font-medium">
                  {user?.name ?? 'Convidado'}
                </p>
                <p className="text-ink-4 truncate text-[11px]">{user?.email ?? 'sem sessão'}</p>
              </div>
              <ChevronUp
                className={cn(
                  'text-ink-3 size-4 shrink-0 transition-transform duration-200',
                  userMenuOpen ? 'rotate-0' : 'rotate-180',
                )}
                aria-hidden="true"
              />
            </button>
          </PopoverTrigger>

          <PopoverContent
            {...(portalContainer ? { portalContainer } : {})}
            side="top"
            align="start"
            sideOffset={8}
            className="w-[var(--radix-popover-trigger-width)] p-2"
          >
            {/* Identity header */}
            <div className="flex items-center gap-2.5 px-1.5 pb-2 pt-1">
              <AvatarGradient initials={initials} src={avatarSrc} size={32} />
              <div className="min-w-0 leading-tight">
                <p className="text-ink truncate text-[13px] font-semibold">
                  {user?.name ?? 'Convidado'}
                </p>
                <p className="text-ink-3 truncate text-[11px]">{user?.email ?? 'sem sessão'}</p>
              </div>
            </div>

            <div className="border-hair my-1.5 h-px" />

            {/* Tema — clicar num segmento NÃO pode fechar o popover (AC-2). */}
            <p className="eyebrow px-1.5 pb-1.5 pt-0.5">Tema</p>
            <div className="px-1.5 pb-1.5">
              <ThemeSegmentedControl compact />
            </div>

            <div className="border-hair my-1.5 h-px" />

            {/* Issue #584 — a troca para as vistas do portal, só a quem as tem. */}
            {hasPortalViews(user?.portalViews) ? (
              <>
                <PortalViewSwitcher
                  views={user.portalViews}
                  current={null}
                  onNavigate={() => setUserMenuOpen(false)}
                />
                <div className="border-hair my-1.5 h-px" />
              </>
            ) : null}

            <Link
              to="/settings"
              onClick={() => setUserMenuOpen(false)}
              className="text-ink hover:bg-sunk flex w-full items-center gap-2.5 rounded-sm px-2.5 py-2 text-[13px] font-medium transition-colors"
            >
              <Settings className="text-ink-3 size-4 shrink-0" aria-hidden="true" />
              Configurações
            </Link>

            {user ? (
              <button
                type="button"
                onClick={() => {
                  setUserMenuOpen(false)
                  void signOut()
                }}
                className="text-error hover:bg-sunk flex w-full items-center gap-2.5 rounded-sm px-2.5 py-2 text-[13px] font-medium transition-colors"
              >
                <LogOut className="size-4 shrink-0" aria-hidden="true" />
                Sair
              </button>
            ) : null}

            {/* Story 32.1 — a versão desta build do web: é o que se diz quando se
                reporta um problema. Bate com a do /api/health quando a API e o
                web sobem do mesmo commit. Discreta, no fim do menu; o
                `text-ink-3` lê `--ink-2` dentro do vidro flutuante. */}
            <div className="border-hair my-1.5 h-px" />
            <p className="text-ink-3 px-2.5 pb-1 pt-0.5 text-[11px]">
              Versão <span className="num">{import.meta.env.VITE_APP_VERSION}</span>
            </p>
          </PopoverContent>
        </Popover>
      </div>
    </nav>
  )
}
